<template>
  <div class="current-subscription">
    <div class="page-header">
      <h1 class="page-title">{{ $t("subscriptions.title") }}</h1>
      <!-- <the-breadcrumb :items="breadcrumbItems" /> -->
    </div>

    <div class="content-wrapper">
      <div v-if="loading" class="loading-container">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <div v-else-if="currentPackage" class="subscription-card">
        <div class="card-header">
          <h2>{{ $t("subscriptions.currentSubscription") }}</h2>
        </div>

        <div class="card-body">
          <div class="package-image">
            <img :src="currentPackage.image" :alt="currentPackage.name" />
          </div>

          <div class="package-details">
            <div class="detail-item">
              <label>{{ $t("subscriptions.packageName") }}</label>
              <p>{{ currentPackage.name }}</p>
            </div>

            <div class="detail-item">
              <label>{{ $t("subscriptions.packagePrice") }}</label>
              <p>
                {{ currentPackage.total_with_tax }} {{ $t("common.currency") }}
              </p>
            </div>

            <div class="detail-item">
              <label>{{ $t("subscriptions.expiryDate") }}</label>
              <p>{{ formatDate(currentPackage.expiry_date) }}</p>
            </div>

            <div class="detail-item">
              <label>{{ $t("subscriptions.packageDescription") }}</label>
              <p>
                {{ currentPackage.description || $t("common.notAvailable") }}
              </p>
            </div>

            <div class="detail-item">
              <label>{{ $t("subscriptions.duration") }}</label>
              <p>
                {{ currentPackage.duration }} {{ $t("subscriptions.months") }}
              </p>
            </div>

            <div class="detail-item status" :class="subscriptionStatus">
              <label>{{ $t("subscriptions.status") }}</label>
              <p>
                <span class="status-badge" :class="subscriptionStatus">
                  {{ $t(`subscriptions.${subscriptionStatus}`) }}
                </span>
              </p>
            </div>
          </div>
        </div>

        <div class="card-footer">
          <base-button
            :label="$t('subscriptions.renewSubscription')"
            type="primary"
            @click="goToPackagesList"
          />
        </div>
      </div>

      <div v-else class="no-subscription">
        <img
          src="@/assets/media/empty_messages/search_message.png"
          alt="No subscription"
        />
        <h3>{{ $t("subscriptions.noActiveSubscription") }}</h3>
        <p>{{ $t("subscriptions.subscribeToAccessServices") }}</p>
        <base-button
          :label="$t('subscriptions.browsePackages')"
          type="primary"
          @click="goToPackagesList"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import TheBreadcrumb from "@/components/structure/TheBreadcrumb.vue";

export default {
  name: "CurrentSubscription",

  components: {
    TheBreadcrumb,
  },

  data() {
    return {
      loading: false,
      currentPackage: null,
    };
  },

  computed: {
    ...mapGetters("subscriptions", ["currentPackage"]),

    breadcrumbItems() {
      return [
        { text: this.$t("common.home"), to: "/" },
        { text: this.$t("subscriptions.title"), active: true },
      ];
    },

    subscriptionStatus() {
      if (!this.currentPackage) return "inactive";
      const expiryDate = new Date(this.currentPackage.expiry_date);
      const now = new Date();
      return expiryDate > now ? "active" : "expired";
    },
  },

  mounted() {
    this.loadCurrentSubscription();
    this.checkQueryParams();
  },

  methods: {
    ...mapActions("subscriptions", ["setCurrentPackage"]),

    checkQueryParams() {
      // Check if redirected due to expired subscription
      if (this.$route.query.expired === "true") {
        this.$message.error(
          this.$t("subscriptions.subscriptionExpiredMessage")
        );
      }

      // Check if redirected due to no subscription
      if (this.$route.query.noSubscription === "true") {
        this.$message.error(this.$t("subscriptions.subscribeToAccessServices"));
      }
    },

    async loadCurrentSubscription() {
      this.loading = true;
      try {
        // In a real scenario, you would have an API endpoint to get current subscription
        // For now, using mock data
        this.currentPackage = {
          id: 1,
          name: "تست",
          image:
            "https://backend.perfermo.moltaqadev.com/storage/5/image-137341-attention-elegance-external-appearance-children-3d-cartoo-search_large.jpg",
          price: "150",
          total_with_tax: 165,
          duration: "1",
          expiry_date: "2026-02-18",
          description: "باقة تجريبية للاشتراك في الخدمات",
        };
      } catch (error) {
        this.$message.error(
          error.message || this.$t("subscriptions.errors.loadFailed")
        );
      } finally {
        this.loading = false;
      }
    },

    formatDate(date) {
      if (!date) return this.$t("common.notAvailable");
      return new Date(date).toLocaleDateString("ar-EG");
    },

    goToPackagesList() {
      this.$router.push({ name: "PackagesList" });
    },
  },
};
</script>

<style lang="scss" scoped>
.current-subscription {
  padding: 20px;

  .page-header {
    margin-bottom: 30px;

    .page-title {
      font-size: 24px;
      font-weight: 600;
      margin-bottom: 10px;
      color: var(--theme_text_clr);
    }
  }

  .content-wrapper {
    .loading-container {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 400px;
    }

    .subscription-card {
      background: var(--theme_second_bg_clr);
      border-radius: 12px;
      box-shadow: 0 2px 8px var(--simple_shadow_clr);
      overflow: hidden;

      .card-header {
        padding: 20px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: #fff;

        h2 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
        }
      }

      .card-body {
        display: grid;
        grid-template-columns: 300px 1fr;
        gap: 30px;
        padding: 30px;

        @media (max-width: 768px) {
          grid-template-columns: 1fr;
        }

        .package-image {
          img {
            width: 100%;
            height: 300px;
            object-fit: cover;
            border-radius: 8px;
          }
        }

        .package-details {
          display: flex;
          flex-direction: column;
          gap: 20px;

          .detail-item {
            label {
              display: block;
              font-size: 14px;
              font-weight: 600;
              color: var(--theme_secondary_text_clr);
              margin-bottom: 8px;
            }

            p {
              margin: 0;
              font-size: 16px;
              color: var(--theme_text_clr);
            }

            &.status {
              .status-badge {
                padding: 6px 12px;
                border-radius: 20px;
                font-size: 14px;
                font-weight: 600;

                &.active {
                  background: #d4edda;
                  color: #155724;
                }

                &.expired {
                  background: #f8d7da;
                  color: #721c24;
                }

                &.inactive {
                  background: #e2e3e5;
                  color: #383d41;
                }
              }
            }
          }
        }
      }

      .card-footer {
        padding: 20px 30px;
        border-top: 1px solid var(--border_clr);
        display: flex;
        justify-content: flex-end;
      }
    }

    .no-subscription {
      text-align: center;
      padding: 60px 20px;
      background: var(--theme_second_bg_clr);
      border-radius: 12px;
      box-shadow: 0 2px 8px var(--simple_shadow_clr);

      img {
        width: 200px;
        margin-bottom: 30px;
      }

      h3 {
        font-size: 22px;
        font-weight: 600;
        color: var(--theme_text_clr);
        margin-bottom: 10px;
      }

      p {
        font-size: 16px;
        color: var(--theme_secondary_text_clr);
        margin-bottom: 30px;
      }
    }
  }
}
</style>
